import { useState } from "react";
import { Button } from "../Button";
import { DueDiligenceFeedbackQuestionsConfig } from "../../Common/Hooks/useApi/Schemas";
import {
  NpsRow,
  NpsCircle,
  NpsScaleHint,
  QuestionLabel,
  QuestionBlock,
  AnswerTextarea,
  ChoiceRow,
  ChoiceButton,
  FormActions,
  ManualFeedbackButton,
} from "./Styles";

const NPS_VALUES = Array.from({ length: 11 }, (_, i) => i);

interface FeedbackFormProps {
  config: DueDiligenceFeedbackQuestionsConfig;
  isSubmitting: boolean;
  onSubmit: (data: { npsScore: number; answers: Record<string, string> }) => void;
  // Only set by callers that open this form in a plain modal (no visible X to
  // close it) — the auto-prompt banner already has its own close affordance.
  onClose?: () => void;
  // Pre-selects a score, e.g. when opened from the footer prompt's own circles.
  initialNpsScore?: number | null;
}

export const FeedbackForm = ({ config, isSubmitting, onSubmit, onClose, initialNpsScore = null }: FeedbackFormProps) => {
  const [npsScore, setNpsScore] = useState<number | null>(initialNpsScore);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const handleAnswerChange = (questionId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    if (npsScore === null) return;
    onSubmit({ npsScore, answers });
  };

  const sortedQuestions = [...config.questions].sort((a, b) => a.order - b.order);

  return (
    <div>
      <QuestionLabel>{config.npsLabel}</QuestionLabel>
      <NpsRow>
        {NPS_VALUES.map((value) => (
          <NpsCircle
            key={value}
            type="button"
            $selected={npsScore === value}
            onClick={() => setNpsScore(value)}
          >
            {value}
          </NpsCircle>
        ))}
      </NpsRow>
      <NpsScaleHint>
        <span>Nada probable</span>
        <span>Muy probable</span>
      </NpsScaleHint>

      {sortedQuestions.map((question) => (
        <QuestionBlock key={question.id}>
          <QuestionLabel>{question.label}</QuestionLabel>
          {question.type === "text" ? (
            <AnswerTextarea
              value={answers[question.id] ?? ""}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            />
          ) : (
            <ChoiceRow>
              {(question.options ?? []).map((option) => (
                <ChoiceButton
                  key={option}
                  type="button"
                  $selected={answers[question.id] === option}
                  onClick={() => handleAnswerChange(question.id, option)}
                >
                  {option}
                </ChoiceButton>
              ))}
            </ChoiceRow>
          )}
        </QuestionBlock>
      ))}

      <FormActions $spread={!!onClose}>
        {onClose && (
          <ManualFeedbackButton type="button" onClick={onClose}>
            Cerrar
          </ManualFeedbackButton>
        )}
        <Button disabled={npsScore === null} isLoading={isSubmitting} onClick={handleSubmit}>
          Enviar feedback
        </Button>
      </FormActions>
    </div>
  );
};
